import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {tokens, fonts, radii, colors} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React, {useState} from 'react'
import styled, {css} from 'styled-components'

import {SignupModal} from '@/components/signup-modal'
import type {PricingFragment} from '@/graphql-types'
import numberOneTrophy from '@/images/icons/number-one-trophy.svg'
import {wait} from '@/utils/wait'
import Button from '@helios/shared/components/button'
import useNavigate from '@helios/shared/hooks/use-navigate'
import checkIconPink from '@helios/shared/images/icons/check-pink.svg'
import {text, StyledText, Text} from '@helios/shared/styles/typography'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

export interface Props {
    withCtas?: boolean
}

interface PricingProps extends Props {
    content: PricingFragment['data']
    mode: BillingCycle
}

export enum Tier {
    Starter = 'starter',
    Essential = 'essential',
    Advanced = 'advanced',
}

export enum BillingCycle {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

export const Pricing = ({content, mode = BillingCycle.Monthly, withCtas = true}: PricingProps) => {
    const [activeItemIndex, setActiveItemIndex] = useState(0)
    const [activeSignupModal, setActiveSignupModal] = React.useState<Tier | null>(null)
    const tiers = content.pricing_tiers.map(({pricing_tier}) => pricing_tier.document.data)
    const navigate = useNavigate()

    const getTierName = (index: number): Tier => {
        switch (index) {
            case 0:
                return Tier.Starter

            case 1:
                return Tier.Essential

            case 2:
                return Tier.Advanced

            default:
                throw new Error('Unrecognized tier')
        }
    }

    const handleAdvancedTierClick = async () => {
        track(TrackingEvent.PlanSelected, {
            value: Tier.Advanced,
        })

        await wait(300)

        navigate('/demo')
    }

    const formId = getEnvironmentFormId(
        content.hubspot_form_id_production,
        content.hubspot_form_id_staging,
        process.env.GATSBY_HUBSPOT_SIGNUP_FORM_ID
    )

    return (
        <OuterWrapper>
            <Wrapper activeItemIndex={activeItemIndex}>
                <Tabs>
                    {tiers.map((tier, i) => (
                        <Tab
                            key={tier.name}
                            isActive={i === activeItemIndex}
                            onClick={() => setActiveItemIndex(i)}
                        >
                            <Text as="span" center>
                                {tier.name}
                            </Text>
                        </Tab>
                    ))}
                </Tabs>

                <Title>
                    {tiers.map((tier) => (
                        <TiersTitle key={tier.name}>
                            <Text as="h3" variant="heading-base">
                                {tier.name}
                            </Text>
                        </TiersTitle>
                    ))}
                </Title>

                <Amount withCtas={withCtas}>
                    {tiers.map((tier) => {
                        const billingCycleCost =
                            mode === BillingCycle.Monthly ? tier.monthly_cost : tier.annual_cost

                        return (
                            <Box key={tier.name}>
                                <AmountText color="muted" variant="paragraph-large">
                                    {mode === BillingCycle.Yearly &&
                                    !tier.name.toLowerCase().includes('starter') ? (
                                        <>
                                            <del>
                                                <VisuallyHidden>
                                                    {tier.monthly_billing_screen_reader_alt}
                                                </VisuallyHidden>
                                                {tier.monthly_cost}
                                            </del>{' '}
                                        </>
                                    ) : null}
                                    <ins>
                                        <VisuallyHidden>
                                            {tier.annual_billing_screen_reader_alt}
                                        </VisuallyHidden>
                                        {billingCycleCost}
                                    </ins>{' '}
                                    {tier.per_month_text}
                                </AmountText>
                                <PerUserPricingDetails>
                                    <Text variant="label-base" as="span">
                                        {tier.cost_subtext}
                                    </Text>
                                    <Text variant="label-base" as="span">
                                        {mode === BillingCycle.Yearly
                                            ? tier.annual_cost_subtext_extra
                                            : tier.monthly_cost_subtext_extra}
                                    </Text>
                                </PerUserPricingDetails>
                            </Box>
                        )
                    })}
                </Amount>

                <Excerpt>
                    {tiers.map((tier) => (
                        <Box key={tier.name}>
                            <Text variant="paragraph-base" color="lightMuted">
                                {tier.excerpt}
                            </Text>
                        </Box>
                    ))}
                </Excerpt>

                {withCtas && (
                    <Cta>
                        {tiers.map((tier, i) => (
                            <Box key={tier.name}>
                                {i === tiers.length - 1 ? (
                                    <Button
                                        data-testid={`pricing-tier-cta-${tier.name}`}
                                        onClick={handleAdvancedTierClick}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.cta_text}
                                    </Button>
                                ) : (
                                    <Button
                                        data-testid={`pricing-tier-cta-${tier.name}`}
                                        onClick={() => {
                                            track(TrackingEvent.PlanSelected, {
                                                value: getTierName(i),
                                            })

                                            setActiveSignupModal(getTierName(i))
                                        }}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.cta_text}
                                    </Button>
                                )}
                            </Box>
                        ))}
                    </Cta>
                )}

                {hasNonEmptyItems(content.signup_modal_free) && (
                    <SignupModal
                        {...content.signup_modal_free[0]}
                        formId={formId}
                        isOpen={activeSignupModal === Tier.Starter}
                        onDismiss={() => setActiveSignupModal(null)}
                        signupProps={{
                            pricing_plan: Tier.Starter,
                        }}
                        highlightedFeatures={[
                            content.signup_modal_free[0].highlighted_feature_1,
                            content.signup_modal_free[0].highlighted_feature_2,
                            content.signup_modal_free[0].highlighted_feature_3,
                        ].filter((item) => Boolean(item))}
                    />
                )}

                {hasNonEmptyItems(content.signup_modal_pro) && (
                    <SignupModal
                        {...content.signup_modal_pro[0]}
                        formId={formId}
                        isOpen={activeSignupModal === Tier.Essential}
                        onDismiss={() => setActiveSignupModal(null)}
                        signupProps={{
                            pricing_plan: Tier.Essential,
                        }}
                        highlightedFeatures={[
                            content.signup_modal_pro[0].highlighted_feature_1,
                            content.signup_modal_pro[0].highlighted_feature_2,
                            content.signup_modal_pro[0].highlighted_feature_3,
                        ].filter((item) => Boolean(item))}
                    />
                )}

                <FeaturesTitle>
                    {tiers.map((tier) => (
                        <Box key={tier.name}>
                            <Text>{tier.features_title}</Text>
                        </Box>
                    ))}
                </FeaturesTitle>

                <Row>
                    {tiers.map((tier) => (
                        <Box key={tier.name}>
                            <FeaturesList>
                                {tier.features.map(
                                    ({feature, disabled_monthly, is_highlighted}) => (
                                        <FeaturesItem
                                            $isHighlighted={
                                                is_highlighted ||
                                                (disabled_monthly && mode === BillingCycle.Yearly)
                                            }
                                            $isDisabled={
                                                disabled_monthly && mode !== BillingCycle.Yearly
                                            }
                                            key={feature}
                                        >
                                            {feature}
                                        </FeaturesItem>
                                    )
                                )}
                            </FeaturesList>
                        </Box>
                    ))}
                </Row>
            </Wrapper>
        </OuterWrapper>
    )
}

const PerUserPricingDetails = styled.div`
    display: grid;
    gap: ${tokens.spacing4};
`

const Row = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    column-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`

const Box = styled.div`
    display: grid;
    gap: ${tokens.spacing8};
    justify-content: start;
    align-content: start;
    padding-right: ${tokens.spacing20};
    padding-left: ${tokens.spacing20};
    background-color: ${colors.background.default};

    ${mq.max('sm')} {
        ${Row}:nth-of-type(2) & {
            padding-top: ${tokens.spacing20};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing20};
        }
    }

    ${mq.min('sm')} {
        ${Row}:first-of-type & {
            padding-top: ${tokens.spacing40};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing40};
        }
    }

    ${mq.min('lg')} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const TiersTitle = styled(Box)`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`

const Title = styled(Row)`
    ${Box} {
        align-items: center;
        display: flex;
        padding-bottom: ${tokens.spacing12};

        :nth-child(2) {
            position: relative;

            ::after {
                position: absolute;
                top: -${tokens.spacing20};
                right: ${tokens.spacing20};
                width: 80px;
                height: 80px;
                content: '';
                background-color: ${colors.background.default};
                background-image: url(${numberOneTrophy});
                background-repeat: no-repeat;
                background-position: center;
                background-size: 80%;
                border-radius: ${radii.arc8};
                box-shadow: ${tokens.shadowRaiseSubtle};

                ${mq.min('lg')} {
                    right: ${tokens.spacing40};
                }
            }
        }
    }

    ${mq.max('sm')} {
        display: none;
    }
`

type AmountProps = Pick<Props, 'withCtas'>

const Amount = styled(Row)<AmountProps>`
    ${Box} {
        display: grid;
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${(props) => (props.withCtas ? tokens.spacing20 : tokens.spacing40)};
        }
    }
`

const AmountText = styled(Text)`
    del {
        color: ${colors.text.extraMuted};
    }

    ins {
        text-decoration: none;
    }
`

const Excerpt = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

const Cta = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        > * {
            width: 100%;

            ${mq.min('xs')} {
                width: auto;
            }
        }

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing40};
        }
    }
`

const FeaturesTitle = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing12};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

const FeaturesList = styled.ul`
    display: grid;
    padding: 0;
    margin: 0;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing24};
    }
`

interface FeaturesItemProps {
    $isHighlighted: boolean
    $isDisabled: boolean
}

const FeaturesItem = styled.li<FeaturesItemProps>(({$isHighlighted, $isDisabled}) => {
    const highlightedStyles = css`
        color: ${colors.text.brand};
    `

    const disabledStyles = css`
        color: ${colors.text.extraMuted};
        text-decoration: line-through;

        /* This combination's outcome is #CCC */
        ::before {
            filter: grayscale(1);
            opacity: 0.42;
        }
    `

    return css`
        display: flex;
        ${text({variant: 'paragraph-base'})}
        ${$isHighlighted && highlightedStyles}
        ${$isDisabled && disabledStyles}

        :before {
            margin-right: ${tokens.spacing12};
            content: url(${checkIconPink});
        }
    `
})

const Tabs = styled.nav`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: ${tokens.spacing8};
    gap: ${tokens.spacing8};

    ${mq.min('sm')} {
        display: none;
    }
`

const isActive = css`
    ${StyledText} {
        font-weight: ${fonts.weights.semiBold};
        text-decoration: underline;
    }
`

const OuterWrapper = styled.div`
    position: relative;
    width: 100%;
`

interface TabProps {
    isActive: boolean
}

const Tab = styled.button<TabProps>`
    ${text({color: 'default'})}
    padding: ${tokens.spacing20} 0;
    cursor: pointer;
    background: ${colors.background.default};
    border: none;

    ${(props) => props.isActive && isActive}
`

interface WrapperProps {
    activeItemIndex: number
}

const Wrapper = styled.div<WrapperProps>`
    ${mq.max('sm')} {
        ${Box}:not(:nth-of-type(${(props) => props.activeItemIndex + 1})) {
            display: none;
        }
    }
`

export const pricingFragment = graphql`
    fragment Pricing on PrismicPricingTiers {
        lang
        data {
            title_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            billing_cycle {
                billed_monthly_label
                billed_yearly_label
                description
                legend
            }
            hubspot_form_id_staging
            hubspot_form_id_production
            signup_modal_free {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            signup_modal_pro {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            pricing_tiers {
                pricing_tier {
                    ... on PrismicLinkType {
                        document {
                            ... on PrismicPricingTier {
                                data {
                                    name
                                    per_month_text
                                    cost_subtext
                                    excerpt
                                    cta_text
                                    features_title
                                    features {
                                        feature
                                        disabled_monthly
                                        is_highlighted
                                    }
                                    annual_cost
                                    monthly_cost
                                    monthly_billing_screen_reader_alt
                                    annual_billing_screen_reader_alt
                                    monthly_cost_subtext_extra
                                    annual_cost_subtext_extra
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default Pricing
